import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input, Label, Textarea } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { toast } from "sonner";
import { Megaphone, Send } from "lucide-react";
import { PageShell } from "./PageShell";

export default function PlatformAnnouncements() {
  const [tenants, setTenants] = useState([]);
  const [history, setHistory] = useState([]);
  const [target, setTarget] = useState("all");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/platform/tenants").then((r) => setTenants(r.data.tenants.filter((t) => t.onboarded))).catch(() => {});
    api.get("/platform/announcements").then((r) => setHistory(r.data.announcements)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (subject.trim().length < 3 || body.trim().length < 3) { toast.error("Subject and message are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/platform/announcements", {
        tenant_id: target === "all" ? null : target, subject: subject.trim(), body: body.trim(),
      });
      toast.success(`Announcement sent to ${data.sent_to} tenant(s)`);
      setSubject(""); setBody(""); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <PageShell title="Announcements" testid="platform-announcements-page"
      subtitle="Broadcast platform notices — maintenance windows, new features, policy changes — as in-app notifications to tenant workspaces.">
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="card-flat p-5 space-y-4" data-testid="announcement-compose">
          <div className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-accent" /><h3 className="font-display font-semibold">Compose</h3></div>
          <div>
            <Label>Audience</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="announce-target"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All onboarded tenants ({tenants.length})</SelectItem>
                {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Scheduled maintenance — Sat 2am SGT" data-testid="announce-subject" /></div>
          <div><Label>Message</Label><Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What tenants need to know…" data-testid="announce-body" /></div>
          <Button variant="primary" onClick={send} disabled={busy} data-testid="announce-send">
            <Send className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Sending…" : "Send announcement"}
          </Button>
        </div>

        <div className="card-flat p-5" data-testid="announcement-history">
          <h3 className="font-display font-semibold mb-3">Recent announcements</h3>
          {history.length === 0 ? <div className="text-sm text-muted-foreground">Nothing sent yet.</div> : (
            <div className="space-y-3">
              {history.map((a) => (
                <div key={a.id} className="border border-border rounded-sm p-3" data-testid={`announcement-${a.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{a.subject}</span>
                    <Badge variant={a.scope === "all" ? "success" : "outline"} className="text-[9px] uppercase">{a.scope === "all" ? `All (${a.tenant_count})` : a.tenant_names?.[0] || "1 tenant"}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.body}</p>
                  <div className="text-[10px] font-mono text-muted-foreground mt-1.5">
                    {new Date(a.at).toLocaleString("en-SG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} · {a.by}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
